import { ActionContext } from 'vuex'
import moment from 'moment'
import { handleUnGzipData } from '@/lib/api/use-get-data'
import { getTutoringPlanStaffData } from '@/lib/api/use-get-data'
import {
  getSpecialSupportPlanListByPlanId,
  postSpecialSupportPlan,
  putSpecialSupportPlan,
} from '@/lib/api/specialSupportPlanRepository'
import sessionStorageUtils from '@/lib/sessionStrage/session-strage-utils'
// types
import type {
  SupportItems,
  StaffListType,
  TimeInputValue,
  StateType,
} from '@/components/organisms/specialSupportPlan/details/types/index'
import type { PlanStatusType } from '@/components/organisms/specialSupportPlan/types'
import type {
  SupportItemResponse,
  SupportSchedule,
  PostSpecialSupportPlanParams,
  PutSpecialSupportPlanParams,
  ErrorSpecialSupportPlanResponse,
} from '@/lib/api/types/specialSupportPlan'

interface IdToken {
  office_id: string
  staff_id: string
  use_alert_flg: string
  line_use_flg: string
}

const state: StateType = {
  status: '',
  childName: '',
  assessmentResult: '',
  supportRequiredAreas: '',
  achievementGoals: '',
  supportPlan: '',
  monitoringResult: '',
  supportDetails: [
    {
      supportItemName: '',
      supportGoal: '',
      supportContent: '',
      period: '1ヶ月',
      order: 1,
      monitoringResults: '',
    },
  ],
  planMonitoringResult: '',
  remarks: '',
  alertYearMonth: '',
  previousAlertYearMonth: '',
  consentedPlanDate: '',
  consentedEvaluationDate: '',
  signImage: '',
  createdDate: '',

  isPublic: false,
  isPublished: false,
  isLatest: false,
  isAgentSign: false,
  isKeepConsentedPlanStatus: false,
  managerStaffId: '',

  staffList: [],

  selectTimeArray: [
    {
      days: 'MONDAY',
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      days: 'TUESDAY',
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      days: 'WEDNESDAY',
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      days: 'THURSDAY',
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      days: 'FRIDAY',
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      days: 'SATURDAY',
      startTime: '00:00',
      endTime: '00:00',
    },
    {
      days: 'SUNDAY',
      startTime: '00:00',
      endTime: '00:00',
    },
  ],
  providedInfoRemarks: '',
}

const getters = {
  isEditMonitoring: (state: StateType) => {
    return (
      state.status === 'CONSENTED_PLAN' ||
      (state.status === 'CONSENTED_EVALUATION' && state.isPublished === false)
    )
  },
  isEditableForm: (state: StateType) => {
    return (
      state.status === '' ||
      state.status === 'PLANNING' ||
      (state.status === 'CONSENTED_PLAN' && state.isPublished === false) ||
      (state.status === 'CONSENTED_EVALUATION' && state.isPublished === false)
    )
  },
  processedStaffList: (state: StateType) => {
    return state.staffList.map(staff => {
      return {
        id: staff.id,
        text: staff.name,
        value: staff.id,
      }
    })
  },
  useAlertFlg: () => {
    const officeId = document.cookie.replace(
      /(?:(?:^|.*;\s*)office_id\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    const _idToken = document.cookie.replace(
      /(?:(?:^|.*;\s*)kumasan_idToken\s*=\s*([^;]*).*$)|^.*$/,
      '$1'
    )
    const base64Url = _idToken.split('.')[1]
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/')
    const decode = JSON.parse(decodeURIComponent(escape(window.atob(base64))))
    const decodedIdToken: IdToken[] = handleUnGzipData(
      decode.data.substring(1, decode.data.length - 1)
    )
    const filteredOfficeArray = decodedIdToken.filter(
      idToken => idToken.office_id === officeId
    )
    const unpackedOffice = filteredOfficeArray[0]

    return unpackedOffice.use_alert_flg
  },
  isSelectTimeValid: (state: StateType) => {
    return state.selectTimeArray.every(time => {
      // "00:00" - "00:00" の場合は例外として true
      if (time.startTime === '00:00' && time.endTime === '00:00') {
        return true
      }
      const startTime = moment(time.startTime, 'HH:mm')
      const endTime = moment(time.endTime, 'HH:mm')
      return (
        startTime.isBefore(endTime) && endTime.diff(startTime, 'minutes') <= 420
      )
    })
  },
}

const mutations = {
  setStatus(state: StateType, payload: PlanStatusType) {
    state.status = payload
  },
  setChildName(state: StateType, payload: string) {
    state.childName = payload
  },
  setAssessmentResult(state: StateType, payload: string) {
    state.assessmentResult = payload
  },
  setSupportRequiredAreas(state: StateType, payload: string) {
    state.supportRequiredAreas = payload
  },
  setAchievementGoals(state: StateType, payload: string) {
    state.achievementGoals = payload
  },
  setSupportPlan(state: StateType, payload: string) {
    state.supportPlan = payload
  },
  setMonitoringResult(state: StateType, payload: string) {
    state.monitoringResult = payload
  },
  setSupportDetails(state: StateType, payload: SupportItems[]) {
    state.supportDetails = payload
  },
  addSupportDetail(state: StateType, payload: SupportItems) {
    state.supportDetails.push(payload)
  },
  updateSupportDetail<T extends keyof SupportItems>(
    state: StateType,
    { index, key, value }: { index: number; key: T; value: SupportItems[T] }
  ) {
    if (state.supportDetails[index]) {
      state.supportDetails[index][key] = value
    }
  },
  removeSupportDetail(state: StateType, index: number) {
    state.supportDetails.splice(index, 1)
  },
  setPlanMonitoringResult(state: StateType, payload: string) {
    state.planMonitoringResult = payload
  },
  setRemarks(state: StateType, payload: string) {
    state.remarks = payload
  },
  setCreatedDate(state: StateType, payload: string) {
    state.createdDate = payload
  },
  setAlertYearMouth(state: StateType, payload: string) {
    state.alertYearMonth = payload
  },
  setPreviousAlertYearMonth(state: StateType, payload: string) {
    state.previousAlertYearMonth = payload
  },
  setConsentedPlanDate(state: StateType, payload: string) {
    state.consentedPlanDate = payload
  },
  setSignImage(state: StateType, payload: string) {
    state.signImage = payload
  },
  setConsentedEvaluationDate(state: StateType, payload: string) {
    state.consentedEvaluationDate = payload
  },
  setPublic(state: StateType, payload: boolean) {
    state.isPublic = payload
  },
  setIsAgentSign(state: StateType, payload: boolean) {
    state.isAgentSign = payload
  },
  setManagerStaffId(state: StateType, payload: string) {
    state.managerStaffId = payload
  },
  setStaffList(state: StateType, payload: StaffListType[]) {
    state.staffList = payload
  },
  setSelectTimeArray(state: StateType, payload: TimeInputValue[]) {
    state.selectTimeArray = payload
  },
  setProvidedInfoRemarks(state: StateType, payload: string) {
    state.providedInfoRemarks = payload
  },
  setIsLatest(state: StateType, payload: boolean) {
    state.isLatest = payload
  },
  setIsPublic(state: StateType, payload: boolean) {
    state.isPublic = payload
  },
  setPublished(state: StateType, payload: boolean) {
    state.isPublished = payload
  },
  setIsKeepConsentedPlanStatus(state: StateType, payload: boolean) {
    state.isKeepConsentedPlanStatus = payload
  },
}

type RootState = {}
type SpecialSupportPlanActionContext = ActionContext<StateType, RootState>

const actions = {
  initState({ commit }: SpecialSupportPlanActionContext) {
    commit('setStatus', '')
    commit('setChildName', '')
    commit('setAssessmentResult', '')
    commit('setSupportRequiredAreas', '')
    commit('setAchievementGoals', '')
    commit('setSupportPlan', '')
    commit('setMonitoringResult', '')
    commit('setSupportDetails', [
      {
        supportItemName: '',
        supportGoal: '',
        supportContent: '',
        period: '1ヶ月',
        order: '',
        monitoringResults: '',
      },
    ])
    commit('setPlanMonitoringResult', '')
    commit('setRemarks', '')
    commit('setAlertYearMouth', '')
    commit('setConsentedPlanDate', '')
    commit('setConsentedEvaluationDate', '')
    commit('setSignImage', '')
    commit('setCreatedDate', '')
    commit('setPublic', false)
    commit('setPublished', false)
    commit('setIsAgentSign', false)
    commit('setIsKeepConsentedPlanStatus', false)
    commit('setManagerStaffId', '')
    commit('setStaffList', [])
    commit('setSelectTimeArray', [
      { days: 'MONDAY', startTime: '00:00', endTime: '00:00' },
      { days: 'TUESDAY', startTime: '00:00', endTime: '00:00' },
      { days: 'WEDNESDAY', startTime: '00:00', endTime: '00:00' },
      { days: 'THURSDAY', startTime: '00:00', endTime: '00:00' },
      { days: 'FRIDAY', startTime: '00:00', endTime: '00:00' },
      { days: 'SATURDAY', startTime: '00:00', endTime: '00:00' },
      { days: 'SUNDAY', startTime: '00:00', endTime: '00:00' },
    ])
    commit('setProvidedInfoRemarks', '')
  },
  updateStatus(
    { commit }: SpecialSupportPlanActionContext,
    payload: PlanStatusType
  ) {
    commit('setStatus', payload)
  },
  updateAssessmentResult(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setAssessmentResult', payload)
  },
  updateSupportRequiredAreas(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setSupportRequiredAreas', payload)
  },
  updateAchievementGoals(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setAchievementGoals', payload)
  },
  updateSupportPlan(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setSupportPlan', payload)
  },
  updateMonitoringResult(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setMonitoringResult', payload)
  },
  addSupportDetail(
    { commit }: SpecialSupportPlanActionContext,
    payload: SupportItems
  ) {
    commit('addSupportDetail', payload)
  },
  createSupportDetail({ commit }: SpecialSupportPlanActionContext) {
    const newSupportDetail = {
      supportItemName: '',
      supportGoal: '',
      supportContent: '',
      period: '',
      monitoringResults: '',
      order: state.supportDetails.length + 1,
    }
    commit('addSupportDetail', newSupportDetail)
  },
  updateSupportDetail<T extends keyof SupportItems>(
    { commit }: SpecialSupportPlanActionContext,
    { index, key, value }: { index: number; key: T; value: SupportItems[T] }
  ) {
    commit('updateSupportDetail', { index, key, value })
  },
  removeSupportDetail(
    { commit }: SpecialSupportPlanActionContext,
    index: number
  ) {
    commit('removeSupportDetail', index)
  },
  updatePlanMonitoringResult(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setPlanMonitoringResult', payload)
  },
  updateRemarks({ commit }: SpecialSupportPlanActionContext, payload: string) {
    commit('setRemarks', payload)
  },
  updateCreatedDate(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setCreatedDate', payload)
  },
  updateAlertYearMonth(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setAlertYearMouth', payload)
  },
  updatePreviousAlertYearMonth(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setPreviousAlertYearMonth', payload)
  },
  updateConsentedPlanDate(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setConsentedPlanDate', payload)
  },
  updateConsentedEvaluationDate(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setConsentedEvaluationDate', payload)
  },
  updatePublic({ commit }: SpecialSupportPlanActionContext, payload: boolean) {
    commit('setPublic', payload)
  },
  updateIsAgentSign(
    { commit }: SpecialSupportPlanActionContext,
    payload: boolean
  ) {
    commit('setIsAgentSign', payload)
  },
  updateManagerStaffId(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setManagerStaffId', payload)
  },
  updateStaffList(
    { commit }: SpecialSupportPlanActionContext,
    payload: StaffListType[]
  ) {
    commit('setStaffList', payload)
  },
  updateSelectTimeArray(
    { commit }: SpecialSupportPlanActionContext,
    payload: TimeInputValue[]
  ) {
    commit('setSelectTimeArray', payload)
  },
  updateProvidedInfoRemarks(
    { commit }: SpecialSupportPlanActionContext,
    payload: string
  ) {
    commit('setProvidedInfoRemarks', payload)
  },
  updateIsPublic(
    { commit }: SpecialSupportPlanActionContext,
    payload: boolean
  ) {
    commit('setPublic', payload)
  },
  updateIsKeepStatus(
    { commit }: SpecialSupportPlanActionContext,
    payload: boolean
  ) {
    commit('setIsKeepConsentedPlanStatus', payload)
  },
  fetchNewCreateSpecialSupportPlanByChildId: async (
    { commit, dispatch }: SpecialSupportPlanActionContext,
    params: {
      planId: string
      childId: string
      officeId: string
    }
  ) => {
    try {
      const { data } = await getSpecialSupportPlanListByPlanId(params)
      if (data.meta !== '0000' || !data.data) {
        throw new Error(
          `データが取得できませんでした: エラーコード ${data.meta}`
        )
      }

      const plan = data.data.specialized_support_plan

      dispatch('initState')
      // 専門的支援計画①
      commit('setChildName', plan.child_name)
      commit('setAssessmentResult', plan.assessment)
      commit('setSupportRequiredAreas', plan.priority_support_area)
      commit('setAchievementGoals', plan.total_goal)
      commit('setSupportPlan', plan.summary_content)
      const supportDetails: SupportItems[] = plan.support_items.map(
        (item: SupportItemResponse) => {
          return {
            supportItemName: item.item_name,
            supportGoal: item.goal,
            supportContent: item.content,
            period: item.period,
            order: item.order,
            monitoringResults: '',
          }
        }
      )
      commit('setSupportDetails', supportDetails)
      commit('setRemarks', plan.remarks)
      commit('setManagerStaffId', plan.manager_staff_id)

      // アラート年月を保存
      commit('setAlertYearMouth', plan.alert_year_month)
      commit('setPreviousAlertYearMonth', plan.alert_year_month)

      // 専門的支援計画②
      const supportSchedule = plan.support_schedules.map(
        (item: SupportSchedule) => {
          return {
            days: item.days,
            startTime: item.start_time,
            endTime: item.end_time,
          }
        }
      )
      commit('setSelectTimeArray', supportSchedule)
      commit('setProvidedInfoRemarks', plan.support_schedules_note)
    } catch (err) {
      const error = err as ErrorSpecialSupportPlanResponse
      return Promise.reject(error)
    }
  },
  fetchStaffList: async (
    { commit }: SpecialSupportPlanActionContext,
    officeId: string
  ) => {
    try {
      const res = await getTutoringPlanStaffData(officeId)

      if (!res?.data.data) {
        throw new Error(
          `データが取得できませんでした: エラーコード ${res?.data.meta}`
        )
      }
      const currentStaff =
        sessionStorageUtils.handleGetSessionStorage('currentStaff')
      const filteredCurrentStaff = res?.data.data.Items.filter(
        (item: {
          office_number: string
          staff_id: string
          last_name: string
          first_name: string
        }) => item.office_number === currentStaff.office_number
      )
      const staffList = filteredCurrentStaff.map(
        (item: {
          office_number: string
          staff_id: string
          last_name: string
          first_name: string
        }) => {
          return {
            id: item.staff_id,
            name: `${item.last_name} ${item.first_name}`,
          }
        }
      )
      commit('setStaffList', staffList)
    } catch (error) {
      return Promise.reject(error)
    }
  },
  fetchSpecialSupportPlanByChildId: async (
    { commit, dispatch }: SpecialSupportPlanActionContext,
    params: {
      planId: string
      childId: string
      officeId: string
    }
  ) => {
    try {
      const { data } = await getSpecialSupportPlanListByPlanId(params)
      if (data.meta !== '0000' || !data.data) {
        throw new Error(
          `データが取得できませんでした: エラーコード ${data.meta}`
        )
      }

      const plan = data.data.specialized_support_plan

      dispatch('initState')
      commit('setStatus', plan.status)
      commit('setChildName', plan.child_name)
      commit('setAssessmentResult', plan.assessment)
      commit('setSupportRequiredAreas', plan.priority_support_area)
      commit('setAchievementGoals', plan.total_goal)
      commit('setSupportPlan', plan.summary_content)
      commit('setMonitoringResult', plan.monitoring_result)
      const supportDetails: SupportItems[] = plan.support_items.map(
        (item: SupportItemResponse) => {
          return {
            supportItemName: item.item_name,
            supportGoal: item.goal,
            supportContent: item.content,
            period: item.period,
            monitoringResults: item.evaluation,
            order: item.order,
          }
        }
      )
      commit('setSupportDetails', supportDetails)
      const supportSchedule = plan.support_schedules.map(
        (item: SupportSchedule) => {
          return {
            days: item.days,
            startTime: item.start_time,
            endTime: item.end_time,
          }
        }
      )
      commit('setSelectTimeArray', supportSchedule)
      commit('setProvidedInfoRemarks', plan.support_schedules_note)
      commit('setRemarks', plan.remarks)

      commit('setCreatedDate', plan.created_date)
      commit('setAlertYearMouth', plan.alert_year_month)
      commit('setConsentedPlanDate', plan.consented_plan_date)
      commit('setConsentedEvaluationDate', plan.consented_evaluation_date)
      commit('setSignImage', plan.sign_image)
      commit('setPlanMonitoringResult', plan.total_evaluation)
      commit('setManagerStaffId', plan.manager_staff_id)
      commit('setIsLatest', plan.is_latest === '1')

      // 公開非公開はユーザー側で切り替えられるため、永続化されているデータとlocalのデータを分ける
      commit('setIsPublic', plan.is_published === '1')
      commit('setPublished', plan.is_published === '1')
      // アラート年月を保存
      commit('setPreviousAlertYearMonth', plan.alert_year_month)
    } catch (err) {
      const error = err as ErrorSpecialSupportPlanResponse
      return Promise.reject(error)
    }
  },
  postSpecialSupportPlan: async (
    { state }: SpecialSupportPlanActionContext,
    params: {
      officeId: string
      childId: string
    }
  ): Promise<void> => {
    try {
      const _params: { childId: string } & PostSpecialSupportPlanParams = {
        officeId: params.officeId,
        childId: params.childId,
        createdDate: moment().format('YYYY-MM-DD'),
        alertYearMonth: state.alertYearMonth,
        assessment: state.assessmentResult,
        prioritySupportArea: state.supportRequiredAreas,
        totalGoal: state.achievementGoals,
        summaryContent: state.supportPlan,
        supportItems: state.supportDetails.map(item => {
          return {
            itemName: item.supportItemName,
            goal: item.supportGoal,
            content: item.supportContent,
            period: item.period,
            evaluation: item.monitoringResults,
            order: Number(item.order),
          }
        }),
        supportSchedules: state.selectTimeArray,
        supportSchedulesNote: state.providedInfoRemarks,
        remarks: state.remarks,
        managerStaffId: state.managerStaffId,
        isPublished: state.isPublic ? '1' : '0',
      }

      await postSpecialSupportPlan(_params)
    } catch (err) {
      const error = err as ErrorSpecialSupportPlanResponse
      return Promise.reject(error)
    }
  },
  putSpecialSupportPlan: async (
    { state }: SpecialSupportPlanActionContext,
    params: {
      officeId: string
      childId: string
      planId: string
    }
  ): Promise<void> => {
    try {
      const _params: {
        childId: string
        planId: string
      } & PutSpecialSupportPlanParams = {
        childId: params.childId,
        planId: params.planId,
        officeId: params.officeId,
        createdDate: moment(state.createdDate).format('YYYY-MM-DD'),
        alertYearMonth: state.alertYearMonth,
        assessment: state.assessmentResult,
        prioritySupportArea: state.supportRequiredAreas,
        totalGoal: state.achievementGoals,
        summaryContent: state.supportPlan,
        monitoringResult: state.monitoringResult,
        supportItems: state.supportDetails.map(item => ({
          itemName: item.supportItemName,
          goal: item.supportGoal,
          content: item.supportContent,
          period: item.period,
          evaluation: item.monitoringResults,
          order: Number(item.order),
        })),
        supportSchedules: state.selectTimeArray,
        supportSchedulesNote: state.providedInfoRemarks,
        remarks: state.remarks,
        totalEvaluation: state.planMonitoringResult,
        isPublished: state.isPublic ? '1' : '0',
        isSigned: state.isAgentSign ? '1' : '0',
        managerStaffId: state.managerStaffId,
        keepStatus: state.isKeepConsentedPlanStatus ? '1' : '0',
        consentedPlanDate: state.consentedPlanDate,
        consentedEvaluationDate: state.consentedEvaluationDate,
      }

      await putSpecialSupportPlan(_params)
    } catch (err) {
      const error = err as ErrorSpecialSupportPlanResponse
      return Promise.reject(error)
    }
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
